import { useCallback, useEffect, useRef, type RefObject } from "react";
import * as d3 from "d3";
import { useMap } from "react-map-gl/mapbox";
import type { MapRef } from "react-map-gl/mapbox";

import type { MapEdge, MapNode } from "@thalamos/aspire-common";

interface FlashEvent {
  sourceId: string;
  targetId: string;
  timestamp: number;
}

interface NeuronFlashManagerProps {
  edges: MapEdge[];
  nodeMap: Map<string, MapNode>;
  mapRef: RefObject<MapRef | null>;
}

/**
 * Manages "neuron flash" animations along edges.
 *
 * Phase 1: Replay mode — simulates flashes from recent edge activity.
 * Phase 2 (future): Accept live Pusher events as the data source.
 */
export function NeuronFlashManager({
  edges,
  nodeMap,
  mapRef,
}: NeuronFlashManagerProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const { current: map } = useMap();
  const queueRef = useRef<FlashEvent[]>([]);
  const activeRef = useRef(false);
  const replayTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const project = useCallback(
    (lng: number, lat: number): [number, number] | null => {
      const m = mapRef.current?.getMap();
      if (!m) return null;
      const pt = m.project([lng, lat]);
      return [pt.x, pt.y];
    },
    [mapRef],
  );

  /** Animate a single flash along an edge */
  const animateFlash = useCallback(
    (event: FlashEvent) => {
      const svg = svgRef.current;
      if (!svg) return;

      const source = nodeMap.get(event.sourceId);
      const target = nodeMap.get(event.targetId);
      if (!source || !target) return;

      const srcPt = project(source.lng, source.lat);
      const tgtPt = project(target.lng, target.lat);
      if (!srcPt || !tgtPt) return;
      // Narrowed here so the ripple drawn in the nested tick() closure below
      // stays non-null (closure capture drops the guard's narrowing).
      const tgtX = tgtPt[0];
      const tgtY = tgtPt[1];

      const sel = d3.select(svg);
      const duration = 1500;

      // Source ripple
      sel
        .append("circle")
        .attr("cx", srcPt[0])
        .attr("cy", srcPt[1])
        .attr("r", 4)
        .attr("fill", "none")
        .attr("stroke", "#ffffff")
        .attr("stroke-width", 2)
        .attr("opacity", 0.8)
        .transition()
        .duration(duration * 0.6)
        .attr("r", 20)
        .attr("opacity", 0)
        .remove();

      // Travelling particle
      const mx = (srcPt[0] + tgtPt[0]) / 2;
      const my = (srcPt[1] + tgtPt[1]) / 2;
      const dx = tgtPt[0] - srcPt[0];
      const dy = tgtPt[1] - srcPt[1];
      const len = Math.sqrt(dx * dx + dy * dy);
      const nx = len > 0 ? (-dy / len) * len * 0.2 : 0;
      const ny = len > 0 ? (dx / len) * len * 0.2 : 0;
      const cx = mx + nx;
      const cy = my + ny;

      // Create a path for the particle to follow
      const pathId = `flash-path-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      const path = sel
        .append("path")
        .attr("id", pathId)
        .attr(
          "d",
          `M ${srcPt[0]} ${srcPt[1]} Q ${cx} ${cy} ${tgtPt[0]} ${tgtPt[1]}`,
        )
        .attr("fill", "none")
        .attr("stroke", "none");

      const pathEl = path.node();
      if (!pathEl) {
        path.remove();
        return;
      }

      const totalLength = pathEl.getTotalLength();

      const particle = sel
        .append("circle")
        .attr("r", 3)
        .attr("fill", "#ffffff")
        .attr("opacity", 0.9)
        .style("filter", "drop-shadow(0 0 4px #ffffff)");

      const trailLength = 5;
      const trails = Array.from({ length: trailLength }, (_, i) =>
        sel
          .append("circle")
          .attr("r", 2 - i * 0.3)
          .attr("fill", "#ffffff")
          .attr("opacity", 0.6 - i * 0.1),
      );

      const startTime = performance.now();

      function tick() {
        const elapsed = performance.now() - startTime;
        const t = Math.min(1, elapsed / duration);
        const eased = d3.easeCubicInOut(t);

        const point = pathEl!.getPointAtLength(eased * totalLength);
        particle.attr("cx", point.x).attr("cy", point.y);

        // Trail particles
        trails.forEach((trail, i) => {
          const trailT = Math.max(0, eased - (i + 1) * 0.03);
          const trailPoint = pathEl!.getPointAtLength(trailT * totalLength);
          trail.attr("cx", trailPoint.x).attr("cy", trailPoint.y);
        });

        if (t < 1) {
          requestAnimationFrame(tick);
        } else {
          // Target ripple
          sel
            .append("circle")
            .attr("cx", tgtX)
            .attr("cy", tgtY)
            .attr("r", 4)
            .attr("fill", "none")
            .attr("stroke", "#ffffff")
            .attr("stroke-width", 2)
            .attr("opacity", 0.8)
            .transition()
            .duration(duration * 0.4)
            .attr("r", 20)
            .attr("opacity", 0)
            .remove();

          // Clean up
          particle.transition().duration(300).attr("opacity", 0).remove();
          trails.forEach((trail) =>
            trail.transition().duration(300).attr("opacity", 0).remove(),
          );
          path.remove();
        }
      }

      requestAnimationFrame(tick);
    },
    [nodeMap, project],
  );

  /** Process queued flash events one at a time with spacing */
  const processQueue = useCallback(() => {
    if (activeRef.current) return;

    const next = queueRef.current.shift();
    if (!next) return;

    activeRef.current = true;
    animateFlash(next);

    // Space out flashes by ~800ms
    setTimeout(() => {
      activeRef.current = false;
      processQueue();
    }, 800);
  }, [animateFlash]);

  /** Enqueue a flash event */
  const enqueueFlash = useCallback(
    (event: FlashEvent) => {
      // Cap the queue to prevent flooding
      if (queueRef.current.length < 20) {
        queueRef.current.push(event);
      }
      processQueue();
    },
    [processQueue],
  );

  // Replay mode: periodically fire flashes from random edges
  useEffect(() => {
    if (edges.length === 0) return;

    // Start replay after a short delay
    const startDelay = setTimeout(() => {
      replayTimerRef.current = setInterval(
        () => {
          const randomEdge = edges[Math.floor(Math.random() * edges.length)];
          if (randomEdge) {
            enqueueFlash({
              sourceId: randomEdge.sourceId,
              targetId: randomEdge.targetId,
              timestamp: Date.now(),
            });
          }
        },
        2000 + Math.random() * 3000,
      ); // Random interval 2–5s
    }, 2000);

    return () => {
      clearTimeout(startDelay);
      if (replayTimerRef.current) {
        clearInterval(replayTimerRef.current);
      }
    };
  }, [edges, enqueueFlash]);

  // Clear all in-flight animations when the map moves or zooms
  useEffect(() => {
    const m = map?.getMap();
    if (!m) return;

    const cancelFlashes = () => {
      const svg = svgRef.current;
      if (!svg) return;
      const sel = d3.select(svg);
      sel.selectAll("*").interrupt().remove();
      queueRef.current.length = 0;
      activeRef.current = false;
    };

    m.on("movestart", cancelFlashes);
    return () => {
      m.off("movestart", cancelFlashes);
    };
  }, [map]);

  return (
    <svg
      ref={svgRef}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        zIndex: 15,
      }}
    />
  );
}
